import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {HorribleHeroesGameConfig} from "../HorribleHeroesGameConfig";
import {HorribleHeroesTextStyles} from "../config/HorribleHeroesTextStyles";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {HorribleHeroesParsedGameData} from "../server/data/HorribleHeroesParsedGameData";
import {BalanceEvent} from "@nolimitcity/slot-game/bin/core/balance/event/BalanceEvent";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {TweenLite} from "gsap";
import {HorribleHeroesParsedInitData} from "../server/data/HorribleHeroesParsedInitData";
import {Helper} from "@nolimitcity/promo-panel/bin/utils/Helper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export class HorribleHeroesReelBackgroundView extends BaseView {
    public _fsBar: PIXI.Sprite;
    private _reelsBg: PIXI.Sprite;
    private _waysBg: PIXI.Sprite;
    private _waysDisplayed: PIXI.BitmapText;
    private _waysImage: PIXI.Sprite;
    private _isPortrait: boolean = false;
    private _xwaysTotalWon: string = '';
    private _xwaysMeter: TimelineSprite;
    private _layer: ScalableLayer;
    private _isXWayExpanded: boolean[];
    private _xwayUpdation: number[] = [];
    private _reelChainsAnim: PIXI.spine.Spine;
    private _shakeStageTween: TweenLite;
    private _reelAreaTween: TweenLite;
    private _level: string;
    private _gameData: HorribleHeroesParsedGameData;
    private _nextMode: string = "";
    private _isQuickStop: boolean = false;
    private _totalScatterCount: number = -1;

    // modified in #326
    public updateXWays(reelId: number): void {
        const newValue: string = this._xwayUpdation[reelId].toString();

        if (this._waysDisplayed.text === newValue) {
            return;
        }

        this._waysDisplayed.text = newValue;

        if (this._xwaysTotalWon > "1024") {
            this._xwaysMeter.getAnimationAutoShowHide(true, true);

            this._waysDisplayed.fontSize = 25;
            this._waysImage.visible = false;
        } else {
            this._waysDisplayed.fontSize = 16;
            this._waysImage.visible = true;
        }

        this.setWaysDisplayedX();
        this._xwaysMeter.position.set(this._waysDisplayed.x + this._waysDisplayed.width / 2 - 5, this._waysDisplayed.y + this._xwaysMeter.height / 4 - 5);
    }

    // modified in #326
    public updateTotalXWays(): void {
        if (this._waysDisplayed.text === this._xwaysTotalWon) {
            return;
        }

        this._waysDisplayed.text = this._xwaysTotalWon;

        if (this._xwaysTotalWon > "1024") {
            if (this._isXWayExpanded.filter(Boolean).length > 0) {
                this._xwaysMeter.getAnimationAutoShowHide(true, true);
            }

            this._waysDisplayed.fontSize = 25;
            this._waysImage.visible = false;
        } else {
            this._waysDisplayed.fontSize = 16;
            this._waysImage.visible = true;
        }

        this.setWaysDisplayedX();
        this._xwaysMeter.position.set(this._waysDisplayed.x + this._waysDisplayed.width / 2 - 5, this._waysDisplayed.y + this._xwaysMeter.height / 4 - 5);
    }

    public setIsQuickstop(quickStop: boolean, totalScatterCount: number): void {
        //#575
        this._isQuickStop = quickStop;
        this._totalScatterCount = totalScatterCount;
    }

    public playChainAnimation(level: string): void {
        if (this._level === level) {
            return;
        }
        // #235
        this._reelChainsAnim.state.addListener({
            complete: () => {
                if (this._level !== level) {
                    //#575
                    // not play if quickstopped with two scatter lands
                    if (this._totalScatterCount > 2 || (this._totalScatterCount == 2 && !this._isQuickStop)) {
                        this._level = level;
                        this._reelChainsAnim.state.clearTracks();
                        this._reelChainsAnim.state.setAnimation(0, "chainsbgIdleLevel" + level, true);
                    }
                }
                this._reelChainsAnim.state.clearListeners();
            }
        });
    }

    public shakeStageEffect(): void {
        if (this._shakeStageTween && this._shakeStageTween.totalProgress() > 0 && this._shakeStageTween.totalProgress() < 1) {
            this._shakeStageTween.totalProgress(1);
        }

        this._shakeStageTween = TweenLite.to(StageManager.stage, 0.4, {y: "+=" + 30, ease: "shakeBounce"});
    }

    public wiggleReelArea(shakeDuration: number): void {
        const reelArea: ScalableLayer = StageManager.getLayer(HorribleHeroesGameConfig.instance.LAYERS.SYMBOLS.name);
        const stickyWild: ScalableLayer = StageManager.getLayer(HorribleHeroesGameConfig.instance.LAYERS.STICKY_WILDS.name);
        this._reelAreaTween = TweenLite.to([reelArea, stickyWild], shakeDuration, {
            x: "+=" + 8,
            y: "+=" + 8,
            ease: "stageWiggle"
        });
    }

    public killShakeTween(): void {
        this._shakeStageTween && this._shakeStageTween.kill();
    }

    /**
     * @inheritDoc
     */
    protected addEventListeners(): void {
        super.addEventListeners && super.addEventListeners();

        EventHandler.addEventListener(this, BalanceEvent.BET, () => this.resetXWays());
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        const gameConfig: HorribleHeroesGameConfig = HorribleHeroesGameConfig.instance;

        this._layer = StageManager.getLayer(gameConfig.LAYERS.REEL_BACKGROUND.name);
        this._reelsBg = this.createSprite("reelArea", [gameConfig.REEL_AREA_WIDTH / 2, gameConfig.GAME_HEIGHT / 2]);
        this._waysBg = this.createSprite("counter", [gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH - 20, 32]);

        this._waysDisplayed = new PIXI.BitmapText("", HorribleHeroesTextStyles.FS_OUTRO_COUNT_UP);
        this._waysDisplayed.name = "winText";
        this._waysDisplayed.text = this._xwaysTotalWon;
        this._waysDisplayed.fontSize = ((this._xwaysTotalWon > "1024") ? 25 : 16);
        this._waysDisplayed.position.set(52 + gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH / 2 + gameConfig.SYMBOL_WIDTH - this._waysDisplayed.width / 2, 7);
        this._layer.addChild(this._waysDisplayed);
        // #326
        this._waysImage = this.createSprite("ways", [0, 50]);
        this._waysImage.visible = (this._xwaysTotalWon === "1024");
        this._waysImage.scale.set(0.5);

        this._xwaysMeter = this.createTimeLineSprite("xwaysMeter");
        this._xwaysMeter.hide();
        this._reelChainsAnim = this.createSpineAnimation("bg_idle_chains", [361, 375]);
        this._reelChainsAnim.state.clearTracks();
        this._reelChainsAnim.state.setAnimation(0, "chainsbgIdleLevel1", true);

        CustomBounce.create("shakeBounce", {strength: 0.8, squash: 0, endAtStart: true, squashID: "shake"});
        CustomWiggle.create("stageWiggle", {wiggles: 90, type: "easeOut"});

        this._fsBar = this.createSprite("fsBar", [360, 729]);
        this._fsBar.visible = false;
    }

    /*reset default xways on win*/
    protected onResize(resizeData: IResizeData): void {
        const gameConfig: HorribleHeroesGameConfig = HorribleHeroesGameConfig.instance;
        if (resizeData.orientation === Orientation.PORTRAIT || !(Helper.isDefaultScreenRatio(NolimitApplication.screenBounds))) {
            //#554 for ipad mini 4:3
            this._waysBg.x = gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH + 50;
            this._waysDisplayed.x = gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH + 50 - this._waysDisplayed.width / 2;
            this._isPortrait = true;
        } else {

            this._isPortrait = false;
            this._waysBg.x = gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH / 2 + this._waysBg.width / 2;
            this._waysDisplayed.x = gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH / 2 + this._waysBg.width / 2 - this._waysDisplayed.width / 2;
        }
        this._waysImage.x = this._waysBg.x; // #326
        this._reelsBg.position.set(gameConfig.REEL_AREA_WIDTH / 2, gameConfig.GAME_HEIGHT / 2 - 74);
    }

    private onInitDataParsed(data: HorribleHeroesParsedInitData): void {
        this._xwaysTotalWon = data.getGameSizeWildMultipliers().toString();
        this._nextMode = data.nextMode;

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    private createSprite(textureName: string, pos: number[]): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(textureName)[0]);
        sprite.anchor.set(0.5, 0.5);
        sprite.name = textureName;
        sprite.position.set(pos[0], pos[1]);
        this._layer.addChild(sprite);
        return sprite;
    }

    private createSpineAnimation(resourceName: string, pos: number[]): PIXI.spine.Spine {
        const sprite = new PIXI.spine.Spine(GameResources.getSpineAsset(resourceName));
        sprite.pivot.set(0.5, 0.5);
        sprite.position.set(pos[0], pos[1]);
        this._layer.addChild(sprite);
        return sprite;
    }

    private createTimeLineSprite(resourceName: string): TimelineSprite {
        const sprite = new TimelineSprite(GameResources.getTextures(resourceName));
        sprite.anchor.set(0.5, 0.5);
        sprite.blendMode = PIXI.BLEND_MODES.ADD;
        this._layer.addChild(sprite);
        return sprite;
    }

    private onGameDataParsed(data: HorribleHeroesParsedGameData): void {
        this._gameData = data;
        this._isXWayExpanded = data.isXWayExpanded;
        let updatedWays = data.getGameSizeWildMultipliers();
        this._xwayUpdation = [updatedWays, updatedWays, updatedWays, updatedWays, updatedWays];
        this._xwaysTotalWon = data.getTotalNumOfWays.toString();
        for (let i: number = 0; i < 5; i++) {
            if (data.getGameSizeXwaysExpand[i] > 4) {
                this._xwayUpdation[i] = (this._xwayUpdation[i] / 4) * data.getGameSizeXwaysExpand[i];
                updatedWays = this._xwayUpdation[i];
            }
            if (i < 4) {
                this._xwayUpdation[i + 1] = updatedWays;
            }
        }

        this._nextMode = data.nextMode;
    }

    // modified in #326 and #420 to fix that.
    private resetXWays(): void {
        const newValue: string = ((this._nextMode === "NORMAL") ? ("1024") : (this._gameData ? this._gameData.getGameSizeWildMultipliers().toString() : this._xwaysTotalWon));
        if (this._waysDisplayed.text === newValue) {
            return;
        }

        const duration: number = 0.2;

        if (newValue === "1024") {
            TweenLite.to(this._waysDisplayed, duration, {
                alpha: 0, onComplete: () => {
                    this._waysDisplayed.text = newValue;
                    this._waysDisplayed.fontSize = 16;
                    this.setWaysDisplayedX();

                    this._waysImage.visible = true;
                    this._waysImage.alpha = 0;
                }
            });
            TweenLite.to([this._waysDisplayed, this._waysImage], duration, {alpha: 1, delay: duration});
        } else {
            this._waysDisplayed.text = newValue;
            this._waysDisplayed.fontSize = 25;
            this._waysImage.visible = false;

            this.setWaysDisplayedX();
        }
    }

    private setWaysDisplayedX(): void {
        const gameConfig: HorribleHeroesGameConfig = HorribleHeroesGameConfig.instance;

        this._waysDisplayed.x = gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH / 2 + this._waysBg.width / 2 - this._waysDisplayed.width / 2;
        if (this._isPortrait) {
            this._waysDisplayed.x = gameConfig.REEL_AREA_POS_X + gameConfig.REEL_AREA_WIDTH - gameConfig.SYMBOL_WIDTH + 50 - this._waysDisplayed.width / 2;
        }
    }
}